import React, { useEffect, useMemo, useState } from "react";
import {
  IconCheck,
  IconCalendarFilled,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheckFilled,
  IconHome2Filled,
  IconHistory,
  IconMapPin,
  IconPencilFilled,
  IconPhone,
  IconPlus,
  IconReceiptFilled,
  IconSearch,
  IconSettings,
  IconTool,
  IconTrashXFilled,
  IconUserFilled,
  IconX,
} from "@tabler/icons-react";

import ClienteForm from "../components/ClienteForm";
import ConexionForm from "../components/ConexionForm";
import CuentaForm from "../components/CuentaForm";
import Modal from "../components/Modal";
import SucursalForm from "../components/SucursalForm";
import ViewsManager from "../components/ViewsManager";
import { api } from "../utils/api";
import {
  CLIENTE_STATUS,
  CONEXION_STATUS,
  PAYMENT_STATUS,
  SUCURSAL_STATUS,
  conexionTone,
  formatDate,
  formatMoneda,
  paymentTone,
  statusTone,
} from "../utils/format";
import { Badge, Button, EmptyState, FieldError, Input, Select, Spinner, Textarea } from "../utils/ui";

const MAX_ACCOUNTS_PER_BRANCH = 10;

function toISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

const accountColors = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-400",
  "bg-purple-500",
  "bg-pink-500",
];

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 rounded-full bg-white/10 py-2.5 pl-4 pr-6">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-black">
        <Icon className="h-5 w-5 text-black" />
      </div>
      <p className="min-w-0 truncate text-sm leading-none">
        <span className="font-bold text-white">{value}</span>{" "}
        <span className="ml-1 text-gray-400">{label}</span>
      </p>
    </div>
  );
}

function Switch({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? "bg-brand" : "bg-white/15"
      }`}
    >
      <span
        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}

function IconAction({ tone = "edit", label, children, ...props }) {
  const tones = {
    edit: "bg-white text-black hover:scale-105",
    conn: "bg-brand text-white hover:scale-105",
    del: "bg-red-600 text-white hover:scale-105",
  };
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform disabled:opacity-50 ${tones[tone]}`}
      {...props}
    >
      {children}
    </button>
  );
}

function PagoForm({ paquetes = [], initial = null, allowKeep = false, onCancel, onSubmit, savingOverride = false }) {
  const defaultPkg = allowKeep
    ? ""
    : initial?.package_id
    ? String(initial.package_id)
    : paquetes[0]
    ? String(paquetes[0].id)
    : "";
  const [packageId, setPackageId] = useState(defaultPkg);
  const [paymentDate, setPaymentDate] = useState(toISODate());
  const [nextPaymentDate, setNextPaymentDate] = useState(addDays(toISODate(), 30));
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isSaving = saving || savingOverride;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onSubmit({
        packageId,
        paymentDate: paymentDate || null,
        nextPaymentDate: nextPaymentDate || null,
        method: method.trim() || null,
        note: note.trim() || null,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
        <Select label="Paquete" value={packageId} onChange={(e) => setPackageId(e.target.value)}>
          {allowKeep && <option value="">Mantener el de cada sucursal</option>}
          {paquetes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {formatMoneda(p.amount, p.currency)}
            </option>
          ))}
        </Select>
        <Input
          label="Fecha de pago"
          type="date"
          value={paymentDate}
          onChange={(e) => {
            setPaymentDate(e.target.value);
            if (nextPaymentDate && e.target.value && e.target.value > nextPaymentDate) {
              setNextPaymentDate(addDays(e.target.value, 30));
            }
          }}
        />
        <Input
          label="Próximo pago"
          type="date"
          value={nextPaymentDate}
          onChange={(e) => setNextPaymentDate(e.target.value)}
        />
        <Input
          label="Método de pago"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          placeholder="Ej. transferencia, efectivo"
        />
        <Textarea
          label="Notas"
          className="md:col-span-2"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Notas internas (opcional)"
          rows={2}
        />
      </div>
      <div className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
        <span className="text-xs text-gray-400">
          {paquetes.find((p) => Number(p.id) === Number(packageId))
            ? `${formatMoneda(
                paquetes.find((p) => Number(p.id) === Number(packageId)).amount,
                paquetes.find((p) => Number(p.id) === Number(packageId)).currency
              )} por paquete`
            : "Cada sucursal mantiene su paquete"}
        </span>
      </div>
      <FieldError message={error} />
      <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isSaving}>
          <IconCheck className="h-4 w-4" stroke={2.2} />
          {isSaving ? "Guardando..." : "Registrar pago"}
        </Button>
      </div>
    </form>
  );
}

function AccountRow({
  cuenta,
  index,
  toggling,
  totalPermisos,
  confirming,
  onToggle,
  onEdit,
  onDelete,
  onCancelDelete,
  onConfirmDelete,
}) {
  const initials = (cuenta.full_name || cuenta.username || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const active = cuenta.status === "active";
  const vistas = cuenta.permission_slugs?.length ?? 0;

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition-colors ${
        confirming ? "border-red-500/40 bg-red-500/10" : "border-white/10 bg-white/5"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-black ${
            accountColors[index % accountColors.length]
          }`}
        >
          {initials || "?"}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium text-white">{cuenta.full_name || "Sin nombre"}</p>
            <span className="text-xs text-gray-400">@{cuenta.username}</span>
          </div>
          <p className="mt-0.5 truncate text-xs text-gray-400">
            {cuenta.role_name ? `${cuenta.role_name} · ` : ""}
            {vistas}/{totalPermisos} vistas
            {cuenta.email ? ` · ${cuenta.email}` : ""}
          </p>
        </div>
      </div>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onCancelDelete}
            className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={toggling}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-60"
          >
            <IconCheck className="h-4 w-4" stroke={2.2} />
            {toggling ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-2.5">
          <IconAction label="Editar cuenta" onClick={() => onEdit(cuenta)}>
            <IconPencilFilled className="h-4 w-4 text-black" />
          </IconAction>
          <IconAction tone="del" label="Eliminar cuenta" onClick={() => onDelete(cuenta)}>
            <IconTrashXFilled className="h-4 w-4 text-white" />
          </IconAction>
          <Switch checked={active} disabled={toggling} onChange={() => onToggle(cuenta)} />
        </div>
      )}
    </div>
  );
}

export default function Sucursales() {
  const [clientes, setClientes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [suscripciones, setSuscripciones] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingCuentaId, setTogglingCuentaId] = useState(null);
  const [banner, setBanner] = useState("");
  const [serverError, setServerError] = useState("");
  const [modal, setModal] = useState(null);
  const [cuentaModal, setCuentaModal] = useState(null);
  const [vistasOpen, setVistasOpen] = useState(false);
  const [conexion, setConexion] = useState(null);
  const [view, setView] = useState("main");
  const [historial, setHistorial] = useState(null);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialError, setHistorialError] = useState("");
  const [pagoFormOpen, setPagoFormOpen] = useState(false);
  const [selectedPaid, setSelectedPaid] = useState(() => new Set());
  const [payingSelected, setPayingSelected] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingCuenta, setDeletingCuenta] = useState(null);
  const [q, setQ] = useState("");

  const loadAll = async (keepSelection = false) => {
    try {
      const [cs, ss, cu, subs, pkgs] = await Promise.all([
        api.getClientes(),
        api.getSucursales(),
        api.getCuentas(),
        api.getSuscripciones(),
        api.getPaquetes(),
      ]);
      setClientes(cs);
      setSucursales(ss);
      setCuentas(cu);
      setSuscripciones(subs);
      setPaquetes(pkgs);
      setSelectedId((prev) => {
        if (keepSelection && prev && cs.some((c) => c.id === prev)) return prev;
        return cs[0]?.id ?? null;
      });
      setBanner("");
    } catch (e) {
      setBanner(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    api
      .getPermisos()
      .then(setPermisos)
      .catch(() => setPermisos([]));
  }, []);

  const filteredClientes = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return clientes;
    return clientes.filter((c) => {
      return (
        c.name?.toLowerCase().includes(query) ||
        c.rfc?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.phone?.toLowerCase().includes(query)
      );
    });
  }, [clientes, q]);

  const selectedClient = clientes.find((c) => c.id === selectedId) || null;
  const selectedBranches = useMemo(
    () => sucursales.filter((s) => s.client_id === selectedId),
    [sucursales, selectedId]
  );
  const clientSubRows = useMemo(
    () => suscripciones.filter((r) => r.client_id === selectedId),
    [suscripciones, selectedId]
  );
  const cuentasByBranch = useMemo(() => {
    const map = {};
    cuentas.forEach((cuenta) => {
      (map[cuenta.branch_id] = map[cuenta.branch_id] || []).push(cuenta);
    });
    return map;
  }, [cuentas]);

  const totalSucursales = sucursales.length;
  const cuentasActivas = cuentas.filter((cuenta) => cuenta.status === "active").length;

  const openModal = (mode) => {
    setServerError("");
    setModal(mode);
  };

  const closeModal = () => {
    if (saving) return;
    setServerError("");
    setModal(null);
  };

  const openCuentaModal = (mode) => {
    setServerError("");
    setCuentaModal(mode);
  };

  const closeCuentaModal = () => {
    if (saving) return;
    setServerError("");
    setCuentaModal(null);
  };

  const closeConexion = () => {
    if (conexion?.saving || conexion?.rotating) return;
    setConexion(null);
  };

  const openConexion = async (branch) => {
    setServerError("");
    setConexion({ branch, data: null, loading: true, saving: false, rotating: false, error: "" });
    try {
      const data = await api.getConexion(branch.id);
      setConexion((prev) =>
        prev && prev.branch.id === branch.id ? { ...prev, data, loading: false } : prev
      );
    } catch (e) {
      setConexion((prev) =>
        prev && prev.branch.id === branch.id ? { ...prev, error: e.message, loading: false } : prev
      );
    }
  };

  const handleSaveConexion = async (payload) => {
    setConexion((prev) => (prev ? { ...prev, saving: true, error: "" } : prev));
    try {
      const data = await api.updateConexion(conexion.branch.id, payload);
      setConexion((prev) => (prev ? { ...prev, data, saving: false } : prev));
      await loadAll(true);
    } catch (e) {
      setConexion((prev) => (prev ? { ...prev, error: e.message, saving: false } : prev));
    }
  };

  const handleRotarLlave = async () => {
    setConexion((prev) => (prev ? { ...prev, rotating: true, error: "" } : prev));
    try {
      const data = await api.rotarLlave(conexion.branch.id);
      setConexion((prev) => (prev ? { ...prev, data, rotating: false } : prev));
      await loadAll(true);
    } catch (e) {
      setConexion((prev) => (prev ? { ...prev, error: e.message, rotating: false } : prev));
    }
  };

  const handleSubmitCliente = async (data) => {
    setSaving(true);
    setServerError("");
    try {
      const created = await api.createCliente(data);
      setClientes((prev) => [...prev, created]);
      setSelectedId(created.id);
      setModal(null);
    } catch (e) {
      setServerError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitSucursal = async (data) => {
    setSaving(true);
    setServerError("");
    try {
      if (modal && modal.mode === "editar") {
        const updated = await api.updateSucursal(modal.sucursal.id, data);
        setSucursales((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      } else {
        const created = await api.createSucursal(data);
        setSucursales((prev) => [...prev, created]);
        setSelectedId(created.client_id);
      }
      await handleSuscripcionesChanged();
      setModal(null);
    } catch (e) {
      setServerError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCuenta = async (data) => {
    setSaving(true);
    setServerError("");
    try {
      if (cuentaModal && cuentaModal.mode === "editar") {
        const updated = await api.updateCuenta(cuentaModal.cuenta.id, data);
        setCuentas((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        const created = await api.createCuenta({ ...data, branch_id: cuentaModal.branchId });
        setCuentas((prev) => [...prev, created]);
      }
      setCuentaModal(null);
    } catch (e) {
      setServerError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await api.deleteSucursal(deleting.id);
      setSucursales((prev) => prev.filter((s) => s.id !== deleting.id));
      setCuentas((prev) => prev.filter((cuenta) => cuenta.branch_id !== deleting.id));
      setDeleting(null);
    } catch (e) {
      setBanner(e.message);
      setDeleting(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCuenta = async () => {
    if (!deletingCuenta) return;
    setSaving(true);
    try {
      await api.deleteCuenta(deletingCuenta.id);
      setCuentas((prev) => prev.filter((c) => c.id !== deletingCuenta.id));
      setDeletingCuenta(null);
    } catch (e) {
      setBanner(e.message);
      setDeletingCuenta(null);
    } finally {
      setSaving(false);
    }
  };

  const toggleCuenta = async (cuenta) => {
    const next = cuenta.status === "active" ? "inactive" : "active";
    setTogglingCuentaId(cuenta.id);
    try {
      const updated = await api.updateCuenta(cuenta.id, { status: next });
      setCuentas((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setBanner("");
    } catch (e) {
      setBanner(e.message);
    } finally {
      setTogglingCuentaId(null);
    }
  };

  const handlePermisosChanged = async () => {
    const [perm, cu] = await Promise.all([api.getPermisos(), api.getCuentas()]);
    setPermisos(perm);
    setCuentas(cu);
    return true;
  };

  const handlePaquetesChanged = async () => {
    const next = await api.getPaquetes();
    setPaquetes(next);
    return true;
  };

  const handleSuscripcionesChanged = async () => {
    const next = await api.getSuscripciones();
    setSuscripciones(next);
    return true;
  };

  const handlePagarSeleccionadas = async (data) => {
    setPayingSelected(true);
    try {
      const keep = !data.packageId;
      if (keep) {
        const sinPaquete = selectedPayRows.filter((r) => !r.sub?.package_id);
        if (sinPaquete.length > 0) {
          throw new Error(
            `Estas sucursales no tienen paquete asignado: ${sinPaquete
              .map((r) => r.name)
              .join(", ")}. Selecciónalas solas y elige un paquete para ellas, o configúralas desde su tarjeta.`
          );
        }
      }
      if (keep) {
        for (const r of selectedPayRows) {
          await api.registrarPago({
            branch_ids: [r.id],
            package_id: Number(r.sub.package_id),
            payment_date: data.paymentDate,
            next_payment_date: data.nextPaymentDate,
            method: data.method,
            notes: data.note,
          });
        }
      } else {
        await api.registrarPago({
          branch_ids: selectedPayRows.map((r) => r.id),
          package_id: Number(data.packageId),
          payment_date: data.paymentDate,
          next_payment_date: data.nextPaymentDate,
          method: data.method,
          notes: data.note,
        });
      }
      await handleSuscripcionesChanged();
      setSelectedPaid(new Set());
      setPagoFormOpen(false);
    } catch (e) {
      throw e;
    } finally {
      setPayingSelected(false);
    }
  };

  const abrirHistorial = async () => {
    setView("historial");
    setHistorial(null);
    setHistorialError("");
    setHistorialLoading(true);
    try {
      setHistorial(await api.getHistorialPagos());
    } catch (e) {
      setHistorialError(e.message);
    } finally {
      setHistorialLoading(false);
    }
  };

  const mesActual = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  };

  const pagosDelMes = (historial || []).filter((h) => String(h.paid_on).startsWith(mesActual()));
  const totalRecaudado = (historial || []).reduce((acc, h) => acc + Number(h.amount || 0), 0);

  const togglePaid = (branchId) => {
    setPagoFormOpen(false);
    setSelectedPaid((prev) => {
      const next = new Set(prev);
      if (next.has(branchId)) next.delete(branchId);
      else next.add(branchId);
      return next;
    });
  };

  const toggleClientPaid = (clientId) => {
    setPagoFormOpen(false);
    const clientBranches = sucursales.filter((s) => s.client_id === clientId).map((s) => s.id);
    setSelectedPaid((prev) => {
      const next = new Set(prev);
      const allSelected = clientBranches.length > 0 && clientBranches.every((id) => next.has(id));
      if (allSelected) {
        clientBranches.forEach((id) => next.delete(id));
      } else {
        clientBranches.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const selectedPayRows = useMemo(
    () =>
      sucursales
        .filter((s) => selectedPaid.has(s.id))
        .map((s) => ({ ...s, sub: suscripciones.find((r) => r.branch_id === s.id) })),
    [sucursales, suscripciones, selectedPaid]
  );
  const selectedTotal = selectedPayRows.reduce((acc, r) => acc + Number(r.sub?.amount || 0), 0);
  const selectedCurrency = selectedPayRows[0]?.sub?.currency;

  return (
    <div>
      {view === "historial" ? (
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Historial de pagos</h1>
            <p className="mt-1 text-sm font-normal text-gray-400">
              Todos los pagos registrados manualmente y desde el POS.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setView("main")}>
              <IconChevronLeft className="h-4 w-4" stroke={2} />
              Volver a clientes
            </Button>
            <Button variant="ghost" onClick={abrirHistorial}>
              <IconHistory className="h-4 w-4" stroke={2} />
              Actualizar
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Clientes</h1>
            <p className="mt-1 text-sm font-normal text-gray-400">
              Gestiona tus clientes, sus sucursales y las cuentas de acceso al POS.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={abrirHistorial}>
              <IconHistory className="h-4 w-4" stroke={2} />
              Historial de pagos
            </Button>
            <button
              type="button"
              aria-label="Configuración del sistema"
              title="Configuración del sistema"
              onClick={() => setVistasOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
            >
              <IconSettings className="h-5 w-5" stroke={1.75} />
            </button>
            <Button onClick={() => openModal({ mode: "cliente" })}>
              Nuevo cliente
            </Button>
          </div>
        </div>
      )}

      {view === "historial" ? (
        <div>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Registros" value={historial?.length ?? 0} icon={IconHistory} />
            <StatCard
              label="Total recaudado"
              value={formatMoneda(totalRecaudado)}
              icon={IconCircleCheckFilled}
            />
            <StatCard label="Pagos este mes" value={pagosDelMes.length} icon={IconCalendarFilled} />
            <StatCard
              label="Monto este mes"
              value={formatMoneda(pagosDelMes.reduce((acc, h) => acc + Number(h.amount || 0), 0))}
              icon={IconReceiptFilled}
            />
          </div>
          {historialLoading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-sm text-gray-400">
              <Spinner /> Cargando historial...
            </div>
          ) : historialError ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {historialError}
            </div>
          ) : !historial || historial.length === 0 ? (
            <div className="rounded-2xl border border-white/15 bg-white/5">
              <EmptyState
                title="Sin pagos registrados"
                description="Cuando registres un pago (manual o desde el POS) aparecerá aquí."
              />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-wider text-gray-400">
                    <th className="px-4 py-2.5 font-medium">Fecha</th>
                    <th className="px-4 py-2.5 font-medium">Sucursal</th>
                    <th className="px-4 py-2.5 font-medium">Cliente</th>
                    <th className="px-4 py-2.5 font-medium">Paquete</th>
                    <th className="px-4 py-2.5 text-right font-medium">Monto</th>
                    <th className="px-4 py-2.5 font-medium">Método</th>
                    <th className="px-4 py-2.5 font-medium">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h) => (
                    <tr
                      key={h.id}
                      className="border-b border-white/5 transition-colors hover:bg-white/5"
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 text-white">{formatDate(h.paid_on)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-medium text-white">{h.branch_name}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-300">{h.client_name}</td>
                      <td className="px-4 py-2.5 text-gray-300">{h.plan_name || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold text-emerald-400">
                        {formatMoneda(h.amount, h.currency)}
                      </td>
                      <td className="px-4 py-2.5 text-gray-300">{h.method || "—"}</td>
                      <td className="max-w-[240px] truncate px-4 py-2.5 text-gray-300">{h.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Clientes" value={clientes.length} icon={IconUserFilled} />
        <StatCard label="Sucursales" value={totalSucursales} icon={IconHome2Filled} />
        <StatCard label="Cuentas activas" value={cuentasActivas} icon={IconCircleCheckFilled} />
      </div>

      {banner && (
        <div className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {banner}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-gray-400">
          <Spinner /> Cargando...
        </div>
      ) : clientes.length === 0 ? (
        <div className="rounded-2xl border border-white/15 bg-white/5">
          <EmptyState
            title="Aún no hay clientes"
            description="Crea tu primer cliente para empezar a gestionar sus sucursales y cuentas."
            action={
              <Button onClick={() => openModal({ mode: "cliente" })}>
                Nuevo cliente
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="mb-4 max-w-sm">
            <div className="relative">
              <IconSearch
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
                stroke={1.75}
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar cliente por nombre, RFC o correo"
                className="pl-9"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <aside className="space-y-3 lg:col-span-4">
              {filteredClientes.length === 0 ? (
                <div className="rounded-2xl border border-white/15 bg-white/5 px-4 py-8 text-center text-sm text-gray-400">
                  Ningún cliente coincide con la búsqueda.
                </div>
              ) : (
                filteredClientes.map((c) => {
                  const branches = sucursales.filter((s) => s.client_id === c.id);
                  const cSubs = suscripciones.filter((r) => r.client_id === c.id);
                  const cAtrasadas = cSubs.filter((r) => r.payment_status === "overdue").length;
                  const cPaid = cSubs.filter((r) => r.payment_status === "paid").length;
                  const cAllSelected =
                    branches.length > 0 && branches.every((s) => selectedPaid.has(s.id));
                  const isSelected = c.id === selectedId;
                  return (
                    <div
                      key={c.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedId(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedId(c.id);
                        }
                      }}
                      className={`cursor-pointer rounded-2xl border px-4 py-4 transition-colors ${
                        isSelected
                          ? "border-brand bg-white/15"
                          : "border-white/15 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{c.name}</p>
                          {c.email || c.phone ? (
                            <p className="mt-0.5 truncate text-xs text-gray-400">
                              {c.email || c.phone || "—"}
                            </p>
                          ) : (
                            <p className="mt-0.5 text-xs text-gray-400">Sin contacto</p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <Badge tone={statusTone(c.status)}>
                            {CLIENTE_STATUS[c.status] || c.status}
                          </Badge>
                          {branches.length > 0 && (
                            <button
                              type="button"
                              aria-label={`Marcar pagadas todas las sucursales de ${c.name}`}
                              title="Marcar pagadas todas sus sucursales"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleClientPaid(c.id);
                              }}
                              className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                                cAllSelected
                                  ? "bg-brand text-white"
                                  : "bg-white/15 text-white/50 hover:bg-white/25 hover:text-white"
                              }`}
                            >
                              <IconCheck className="h-4 w-4" stroke={2.4} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-gray-400">
                          {branches.length} sucursal{branches.length !== 1 ? "es" : ""} ·{" "}
                          {cPaid}/{cSubs.length} pagadas
                          {cAtrasadas > 0 && (
                            <span className="ml-2 text-red-400">
                              · {cAtrasadas} atrasada{cAtrasadas !== 1 ? "s" : ""}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          aria-label={`Añadir sucursal a ${c.name}`}
                          title="Añadir sucursal"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal({ mode: "nueva", clientId: c.id });
                          }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
                        >
                          <IconPlus className="h-4 w-4" stroke={2} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </aside>

            <section className="lg:col-span-8">
              {!selectedClient ? (
                <div className="rounded-2xl border border-white/15 bg-white/5 px-4 py-16 text-center">
                  <IconChevronRight className="mx-auto h-8 w-8 text-gray-500" stroke={1.5} />
                  <p className="mt-3 text-sm font-medium text-white">Selecciona un cliente</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Haz clic en una tarjeta para ver sus sucursales y cuentas.
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl border border-white/15 bg-white/5 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-white">{selectedClient.name}</h2>
                        <Badge tone={statusTone(selectedClient.status)}>
                          {CLIENTE_STATUS[selectedClient.status] || selectedClient.status}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-400">
                        {selectedClient.rfc && <span>RFC: {selectedClient.rfc}</span>}
                        {selectedClient.phone && (
                          <span className="inline-flex items-center gap-1">
                            <IconPhone className="h-3.5 w-3.5" stroke={1.5} /> {selectedClient.phone}
                          </span>
                        )}
                        {selectedClient.email && <span>{selectedClient.email}</span>}
                        {selectedClient.address && (
                          <span className="inline-flex items-center gap-1">
                            <IconMapPin className="h-3.5 w-3.5" stroke={1.5} /> {selectedClient.address}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button onClick={() => openModal({ mode: "nueva", clientId: selectedClient.id })}>
                      Añadir sucursal
                    </Button>
                  </div>

                  {clientSubRows.length > 0 && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                        <span>
                          {clientSubRows.filter((r) => r.payment_status === "paid").length}/
                          {clientSubRows.length} sucursal
                          {clientSubRows.length !== 1 ? "es" : ""} pagadas este mes
                        </span>
                        {clientSubRows.some((r) => r.payment_status === "overdue") && (
                          <span className="text-red-400">· alguna atrasada</span>
                        )}
                        {paquetes[0] && !clientSubRows.some((r) => !r.package_id) && (
                          <span className="hidden sm:inline">
                            · Total mensual:{" "}
                            {formatMoneda(
                              clientSubRows.reduce((acc, r) => acc + Number(r.amount || 0), 0),
                              clientSubRows[0]?.currency
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-5 space-y-3">
                    {selectedBranches.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-white/20 px-4 py-10 text-center">
                        <p className="text-sm font-medium text-white">Sin sucursales aún</p>
                        <p className="mt-1 text-xs text-gray-400">
                          Añade la primera sucursal para este cliente; luego crea sus cuentas de acceso.
                        </p>
                        <div className="mt-4">
                          <Button onClick={() => openModal({ mode: "nueva", clientId: selectedClient.id })}>
                            Añadir sucursal
                          </Button>
                        </div>
                      </div>
                    ) : (
                      selectedBranches.map((s) => {
                        const branchCuentas = cuentasByBranch[s.id] || [];
                        const activeCuentas = branchCuentas.filter((cuenta) => cuenta.status === "active").length;
                        const atLimit = branchCuentas.length >= MAX_ACCOUNTS_PER_BRANCH;
                        const sub = suscripciones.find((r) => r.branch_id === s.id);
                        const payLabel =
                          sub && sub.payment_status === "overdue"
                            ? `Atrasado · ${sub.days_overdue} día${sub.days_overdue !== 1 ? "s" : ""}`
                            : PAYMENT_STATUS[sub?.payment_status] || "Sin pago";
                        return (
                          <div
                            key={s.id}
                            className={`rounded-2xl border p-5 transition-colors ${
                              deleting && deleting.id === s.id
                                ? "border-red-500/40 bg-red-500/10"
                                : selectedPaid.has(s.id)
                                  ? "border-brand bg-brand/10"
                                  : "border-white/10 bg-white/5"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate text-sm font-semibold text-white">{s.name}</p>
                                  <Badge tone={statusTone(s.status)}>
                                    {SUCURSAL_STATUS[s.status] || s.status}
                                  </Badge>
                                  {s.connection_status && (
                                    <Badge tone={conexionTone(s.connection_status)}>
                                      {CONEXION_STATUS[s.connection_status] ||
                                        s.connection_status}
                                    </Badge>
                                  )}
                                  <span className="text-xs text-gray-400">
                                    {branchCuentas.length}/{MAX_ACCOUNTS_PER_BRANCH} cuentas
                                  </span>
                                </div>
                                <p className="mt-1 truncate text-xs text-gray-400">
                                  {[s.city, s.phone, s.address].filter(Boolean).join(" · ") ||
                                    "Sin datos de contacto"}
                                </p>
                                <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                                    {sub ? (
                                      <>
                                        <Badge tone={paymentTone(sub.payment_status)}>
                                          {payLabel}
                                        </Badge>
                                        {sub.plan_name && (
                                          <span>
                                            {sub.plan_name} · {formatMoneda(sub.amount, sub.currency)}
                                          </span>
                                        )}
                                        {sub.next_payment_date && (
                                          <span>Próximo pago: {formatDate(sub.next_payment_date)}</span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-gray-500">Sin pago registrado</span>
                                    )}
</div>
                                </div>
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-2">
                                <button
                                  type="button"
                                  aria-label={`Marcar ${s.name} como pagada`}
                                  title={
                                    selectedPaid.has(s.id)
                                      ? "Quitar selección"
                                      : "Marcar como pagada este mes"
                                  }
                                  onClick={() => togglePaid(s.id)}
                                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                                    selectedPaid.has(s.id)
                                      ? "bg-brand text-white ring-2 ring-brand/30"
                                      : "bg-white/10 text-white/60 hover:bg-white/25 hover:text-white"
                                  }`}
                                >
                                  <IconCheck className="h-4 w-4" stroke={2.4} />
                                </button>
                                {deleting && deleting.id === s.id ? (
                                  <div className="flex shrink-0 items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setDeleting(null)}
                                      className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25"
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleDelete}
                                      disabled={saving}
                                      className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-60"
                                    >
                                      <IconCheck className="h-4 w-4" stroke={2.2} />
                                      {saving ? "Eliminando..." : "Eliminar"}
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex shrink-0 items-center gap-2.5">
                                    <IconAction
                                      tone="conn"
                                      label={`Configurar conexión de ${s.name}`}
                                      onClick={() => openConexion(s)}
                                    >
                                      <IconTool className="h-4 w-4 text-white" stroke={1.75} />
                                    </IconAction>
                                    <IconAction
                                      label="Editar sucursal"
                                      onClick={() => openModal({ mode: "editar", sucursal: s })}
                                    >
                                      <IconPencilFilled className="h-4 w-4 text-black" />
                                    </IconAction>
                                    <IconAction
                                      tone="del"
                                      label="Eliminar sucursal"
                                      onClick={() => {
                                        setServerError("");
                                        setDeleting(s);
                                      }}
                                    >
                                      <IconTrashXFilled className="h-4 w-4 text-white" />
                                    </IconAction>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="mt-3 space-y-2">
                              {branchCuentas.length === 0 ? (
                                <p className="rounded-2xl border border-dashed border-white/20 px-3 py-3 text-xs text-gray-400">
                                  Sin cuentas de acceso para esta sucursal.
                                </p>
                              ) : (
                                branchCuentas.map((cuenta, i) => (
                                  <AccountRow
                                    key={cuenta.id}
                                    cuenta={cuenta}
                                    index={i}
                                    totalPermisos={permisos.length}
                                    toggling={togglingCuentaId === cuenta.id}
                                    confirming={Boolean(deletingCuenta && deletingCuenta.id === cuenta.id)}
                                    onToggle={() => toggleCuenta(cuenta)}
                                    onEdit={(cuenta) => openCuentaModal({ mode: "editar", cuenta })}
                                    onDelete={(cuenta) => {
                                      setServerError("");
                                      setDeletingCuenta(cuenta);
                                    }}
                                    onCancelDelete={() => setDeletingCuenta(null)}
                                    onConfirmDelete={handleDeleteCuenta}
                                  />
                                ))
                              )}
                            </div>

                            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
                              <span className="text-xs text-gray-400">
                                {activeCuentas} activa{activeCuentas !== 1 ? "s" : ""} de {branchCuentas.length}
                              </span>
                              <button
                                type="button"
                                disabled={atLimit}
                                onClick={() => openCuentaModal({ mode: "nueva", branchId: s.id })}
                                className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-strong disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {atLimit ? "Límite de 10 cuentas" : "Añadir cuenta"}
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {selectedPayRows.length > 0 && (
        <div className="fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="w-full max-w-lg rounded-full border border-white/15 bg-black/95 px-5 py-3 shadow-2xl backdrop-blur">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">
                  {selectedPayRows.length} sucursal{selectedPayRows.length !== 1 ? "es" : ""} seleccionada{selectedPayRows.length !== 1 ? "s" : ""}
                </p>
                <p className="truncate text-xs text-gray-400">
                  Total del mes: {formatMoneda(selectedTotal, selectedCurrency)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  aria-label="Limpiar selección"
                  onClick={() => setSelectedPaid(new Set())}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
                >
                  <IconX className="h-4 w-4" stroke={2} />
                </button>
                <Button onClick={() => setPagoFormOpen(true)}>
                  <IconCheck className="h-4 w-4" stroke={2.4} />
                  Registrar pago
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {pagoFormOpen && (
        <Modal
          title="Registrar pagos"
          subtitle={`${selectedPayRows.length} sucursal${selectedPayRows.length !== 1 ? "es" : ""} seleccionada${selectedPayRows.length !== 1 ? "s" : ""} · Total del mes: ${formatMoneda(
            selectedTotal,
            selectedCurrency
          )}`}
          maxWidth="max-w-2xl"
          onClose={() => setPagoFormOpen(false)}
        >
          <PagoForm
            paquetes={paquetes}
            allowKeep
            onCancel={() => setPagoFormOpen(false)}
            onSubmit={handlePagarSeleccionadas}
            savingOverride={payingSelected}
          />
        </Modal>
      )}

      {modal && modal.mode === "nueva" && (
        <Modal title="Nueva sucursal" maxWidth="max-w-2xl" onClose={closeModal}>
          <SucursalForm
            defaultClientId={modal.clientId}
            clientes={clientes}
            paquetes={paquetes}
            onSubmit={handleSubmitSucursal}
            onCancel={closeModal}
            onNeedClients={() => openModal({ mode: "cliente" })}
            saving={saving}
            serverError={serverError}
          />
        </Modal>
      )}

      {modal && modal.mode === "editar" && (
        <Modal title="Editar sucursal" maxWidth="max-w-2xl" onClose={closeModal}>
          <SucursalForm
            initial={modal.sucursal}
            initialSub={suscripciones.find((r) => r.branch_id === modal.sucursal.id) || null}
            clientes={clientes}
            paquetes={paquetes}
            onSubmit={handleSubmitSucursal}
            onCancel={closeModal}
            onNeedClients={() => openModal({ mode: "cliente" })}
            saving={saving}
            serverError={serverError}
          />
        </Modal>
      )}

      {modal && modal.mode === "cliente" && (
        <Modal
          title="Nuevo cliente"
          subtitle="Los clientes agrupan las sucursales de un mismo negocio."
          maxWidth="max-w-2xl"
          onClose={closeModal}
        >
          <ClienteForm
            onSubmit={handleSubmitCliente}
            onCancel={closeModal}
            saving={saving}
            serverError={serverError}
          />
        </Modal>
      )}

      {cuentaModal && cuentaModal.mode === "nueva" && (
        <Modal
          title="Nueva cuenta"
          subtitle={`Cuenta de acceso al POS de la sucursal · hasta ${MAX_ACCOUNTS_PER_BRANCH} por sucursal.`}
          maxWidth="max-w-6xl"
          onClose={closeCuentaModal}
        >
          <CuentaForm
            permisos={permisos}
            onSubmit={handleSubmitCuenta}
            onCancel={closeCuentaModal}
            saving={saving}
            serverError={serverError}
          />
        </Modal>
      )}

      {cuentaModal && cuentaModal.mode === "editar" && (
        <Modal title="Editar cuenta" maxWidth="max-w-6xl" onClose={closeCuentaModal}>
          <CuentaForm
            initial={cuentaModal.cuenta}
            permisos={permisos}
            onSubmit={handleSubmitCuenta}
            onCancel={closeCuentaModal}
            saving={saving}
            serverError={serverError}
          />
        </Modal>
      )}

      {conexion && (
        <Modal
          title={`Conexión · ${conexion.branch.name}`}
          subtitle="Configuración propia de esta sucursal: llave, base de datos y backend del POS."
          maxWidth="max-w-4xl"
          onClose={closeConexion}
        >
          <ConexionForm
            key={conexion.data?.llave ?? "cargando"}
            branch={conexion.branch}
            data={conexion.data}
            loading={conexion.loading}
            saving={conexion.saving}
            rotating={conexion.rotating}
            error={conexion.error}
            onSave={handleSaveConexion}
            onRotate={handleRotarLlave}
            onCancel={closeConexion}
          />
        </Modal>
      )}

      {vistasOpen && (
        <Modal
          title="Configuración del sistema"
          subtitle="Administra las pantallas del POS y los paquetes de precio."
          onClose={() => setVistasOpen(false)}
          maxWidth="max-w-6xl"
        >
          <ViewsManager
            permisos={permisos}
            cuentas={cuentas}
            paquetes={paquetes}
            onChanged={handlePermisosChanged}
            onPaquetesChanged={handlePaquetesChanged}
            onClose={() => setVistasOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
}